import React, {type ReactNode} from 'react';

import {emptyArray} from '@packages/utilities/fp/utils';
import {useSize} from '@packages/utilities/styled';
import {
  useControllableState,
  useUniqueId,
} from '@packages/systems/spring-hooks';

import {AccordionView} from './Accordion.styles';
import type {
  HTMLAttributes,
  SpringTooling,
  SizeContextSize,
} from '../__SharedTypes';
import {AccordionItemContext} from './AccordionItem';

export type AccordionProps = {
  /**
   * Controls if multiple panels can be open at once. When set to `false`, only one panel
   * will show at any given time. If `defaultOpenIndices` are specified, those will override
   * until initial interaction.
   */
  allowMultiple?: boolean;
  /**
   * Array of open panels during initial render.
   */
  defaultOpenIndices?: number[];
  /**
   * The density of the accordion buttons.
   */
  density?: 'comfortable' | 'compact';
  /**
   * When true, shows a top Divider on all the AccordionItems, including the first AccordionItem.
   * When false, hides the top Divider on the first AccordionItem. This prop is useful when some
   * other component directly above the `Accordion` has a border or divider of some sort and you
   * want to avoid showing a double border.
   */
  hasTopDivider?: boolean;
  /**
   * Heading level used for each `AccordionHeader` that wraps each `AccordionButton`. Each
   * `AccordionHeader` within an `Accordion` will have the same heading level. Defaults to `3`
   * (equivalent to an `<h3>` HTML element).
   */
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Custom ID for accordion. If not provided, one will be auto generated to properly
   * attach aria attributes.
   */
  id?: string;
  /**
   * Allows all panels to be collapsed. If false, one Panel will remain open at all
   * times (except default closed state).
   */
  isCollapsible?: boolean;
  /**
   * Callback when an Item is toggled. Returns an array of indicies of open Panels.
   */
  onChange?: (id: number[]) => void;
  /**
   * Use for controlled Accordion. Won't work with `allowMultiple` or `isCollapsible`.
   */
  openIndices?: number[];
  size?: SizeContextSize;
  /**
   * Content for Accordion. Immedidate children must be `AccordionItem`[].
   */
  children?: ReactNode;
} & Omit<HTMLAttributes, 'onChange'> &
  SpringTooling;

/**
 * # Accordion
 *
 * Base Accordion container component. Manages open panels and utils to toggle
 * visibility. Follows: https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 *
 * ## Usage
 *
 * ```jsx
 * import {Accordion} from '@packages/systems/spring';
 * ```
 *
 * @component
 */
export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      allowMultiple = true,
      children,
      defaultOpenIndices = emptyArray,
      density = 'compact',
      hasTopDivider = true,
      headingLevel = 3,
      isCollapsible = true,
      id: idProp = undefined,
      onChange,
      openIndices = undefined,
      size: sizeProp,
      ...props
    }: AccordionProps,
    ref
  ) => {
    const accordionId = useUniqueId(Accordion, 'accordion', idProp);
    const size = useSize(sizeProp);
    const [openPanels, setOpenPanels] = useControllableState({
      value: openIndices,
      initialValue: defaultOpenIndices,
      onChange,
    });

    // Hide or Show the passed in panel index
    const togglePanel = (index: number) => {
      const isOpen = openPanels.includes(index);
      let newPanels = [...openPanels];

      // Only one panel open at once
      if (allowMultiple === false) {
        newPanels = [index];
      }

      if (isOpen) {
        newPanels.splice(newPanels.indexOf(index), 1);
      } else {
        newPanels.push(index);
      }

      // One must be open at any given time
      if (isCollapsible === false && newPanels.length === 0) {
        newPanels = [index];
      }

      setOpenPanels(newPanels);
    };

    return (
      <AccordionContext.Provider
        value={{
          density,
          hasTopDivider,
          headingLevel,
          id: accordionId,
          openPanels,
          size,
          togglePanel,
        }}
      >
        <AccordionView {...props} id={accordionId} ref={ref}>
          {React.Children.map(children, (child, index) => {
            return (
              <AccordionItemWrapper index={index}>{child}</AccordionItemWrapper>
            );
          })}
        </AccordionView>
      </AccordionContext.Provider>
    );
  }
);

Accordion.displayName = 'Accordion';
Accordion.toString = AccordionView.toString;

export default Accordion;

type AccordionContextType = {
  density?: 'comfortable' | 'compact';
  hasTopDivider?: boolean;
  headingLevel?: number;
  id?: string;
  openPanels?: number[];
  size?: SizeContextSize;
  togglePanel?: null | ((index: number) => void);
};

export const AccordionContext = React.createContext<AccordionContextType>({
  density: 'compact',
  hasTopDivider: true,
  headingLevel: 3,
  id: '',
  openPanels: [],
  size: 'medium',
  togglePanel: null,
});

type AccordionItemWrapperProps = {
  children: React.ReactNode;
  index: number;
};

/**
 * Wrapper for looped children to attach index and descendant like functionality
 */
const AccordionItemWrapper = ({children, index}: AccordionItemWrapperProps) => {
  const [panelId, setPanelId] = React.useState<any>(null);
  const [buttonId, setButtonId] = React.useState<any>(null);
  const contextValue = {
    index,
    panelId,
    setPanelId,
    buttonId,
    setButtonId,
  } as const;
  return (
    <AccordionItemContext.Provider value={contextValue}>
      {children}
    </AccordionItemContext.Provider>
  );
};
